import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useGame } from '@/context/GameContext';
import SEO from '@/components/layout/SEO';

type Leader = {
    rank: number;
    name: string;
    xp: number;
    level: number;
    streak: number;
    isYou: boolean;
};

const makePlaceholder = (rank: number): Leader => ({
    rank,
    name: 'UNRANKED',
    xp: 0,
    level: 1,
    streak: 0,
    isYou: false,
});

function ordinal(rank: number) {
    return String(rank).padStart(2, '0');
}

export default function LeaderboardPage() {
    const { user } = useAuth();
    const { stats, rank, refetch } = useGame();
    const [leaders, setLeaders] = useState<Leader[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<'week' | 'alltime'>('week');

    async function fetchLeaders(scope: 'week' | 'alltime') {
        if (!supabase) {
            setError('Leaderboard is unavailable right now.');
            setLeaders([]);
            return;
        }

        setError(null);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 10_000);

        try {
            let query = supabase
                .from('player_stats')
                .select('user_id, xp, level, streak_days, handle, last_active')
                .order('xp', { ascending: false })
                .limit(10)
                .abortSignal(controller.signal);

            if (scope === 'week') {
                const weekStart = new Date();
                weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (weekStart.getDay() === 0 ? -6 : 1));
                weekStart.setHours(0, 0, 0, 0);
                query = query.gte('last_active', weekStart.toISOString());
            }

            const { data, error: queryError } = await query;
            if (queryError) throw queryError;

            setLeaders(
                (data ?? []).map((entry: any, index: number) => ({
                    rank: index + 1,
                    name: entry.handle || `Player_${index + 1}`,
                    xp: entry.xp ?? 0,
                    level: entry.level ?? 1,
                    streak: entry.streak_days ?? 0,
                    isYou: entry.user_id === user?.id,
                })),
            );
        } catch (err: any) {
            console.error('Error fetching leaderboard:', err);
            setLeaders([]);
            if (err?.name === 'AbortError') {
                setError('Leaderboard request timed out. Tap refresh to retry.');
            } else {
                setError('Unable to load leaderboard right now.');
            }
        } finally {
            clearTimeout(timeoutId);
        }
    }

    useEffect(() => {
        let active = true;
        const load = async () => {
            setLoading(true);
            await fetchLeaders(view);
            if (active) setLoading(false);
        };

        void load();
        return () => {
            active = false;
        };
    }, [view, user?.id]);

    const handleRefresh = async () => {
        setLoading(true);
        await Promise.all([
            fetchLeaders(view),
            refetch ? refetch().catch(() => {}) : Promise.resolve(),
        ]);
        setLoading(false);
    };

    const podium = [
        leaders[1] ?? makePlaceholder(2),
        leaders[0] ?? makePlaceholder(1),
        leaders[2] ?? makePlaceholder(3),
    ];
    const listRows = leaders.slice(3);
    const currentUser = stats
        ? {
            rank: rank,
            name: stats.handle ?? user?.provider_id ?? 'PLAYER_01',
            xp: stats.xp,
            level: stats.level,
            streak: stats.streak_days,
        }
        : null;
    const currentUserVisible = leaders.some((leader) => leader.isYou);

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#0e0e0e] text-white">
            <SEO title="Hall of Fame — startzone.dev" />

            <div className="pointer-events-none fixed inset-0 opacity-[0.16]" style={{ background: 'linear-gradient(to bottom, transparent 50%, rgba(0, 207, 252, 0.05) 50%)', backgroundSize: '100% 4px' }} />
            <div className="pointer-events-none fixed inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(#8cacff 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }} />
            <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0),rgba(0,0,0,0.2)_100%)]" />

            <main className="relative z-10 mx-auto flex min-h-[calc(100vh-5rem)] max-w-[96rem] flex-col px-4 pb-16 pt-24 md:px-6 lg:px-12">
                <header className="mb-12">
                    <div className="flex flex-col justify-between gap-6 border-b border-[#484848]/20 pb-8 md:flex-row md:items-end">
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <span className="h-2 w-2 animate-pulse bg-[#00cffc]" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00cffc]">
                                    SYSLOG_STATUS: ONLINE
                                </span>
                            </div>
                            <h1 className="font-['Space_Grotesk'] text-4xl font-black uppercase tracking-tighter text-white md:text-[4.5rem] md:leading-[0.9]">
                                HALL_OF_FAME <span className="text-[#484848]">//</span> <span className="text-[#719bff]">GLOBAL_RANKINGS</span>
                            </h1>
                            <p className="mt-4 max-w-2xl font-mono text-xs uppercase tracking-tight text-[#acabaa]">
                                Authorized personnel only. Displaying top-tier operatives in the startzone ecosystem.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-2 bg-[#131313] p-1">
                            <button
                                type="button"
                                onClick={() => setView('week')}
                                className={`px-4 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-widest transition-all ${
                                    view === 'week'
                                        ? 'border border-[#00cffc]/30 bg-[#1f2020] text-[#00cffc] shadow-[0_0_10px_rgba(0,207,252,0.1)]'
                                        : 'text-[#acabaa] hover:text-white'
                                }`}
                            >
                                This Week
                            </button>
                            <button
                                type="button"
                                onClick={() => setView('alltime')}
                                className={`px-4 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-widest transition-all ${
                                    view === 'alltime'
                                        ? 'border border-[#00cffc]/30 bg-[#1f2020] text-[#00cffc] shadow-[0_0_10px_rgba(0,207,252,0.1)]'
                                        : 'text-[#acabaa] hover:text-white'
                                }`}
                            >
                                All Time
                            </button>
                            <span className="hidden px-4 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-widest text-[#acabaa]/40 md:inline-flex">
                                Regional
                            </span>
                            <span className="hidden px-4 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-widest text-[#acabaa]/40 md:inline-flex">
                                Friends
                            </span>
                        </div>
                    </div>
                </header>

                {loading ? (
                    <div className="grid min-h-[18rem] place-items-center">
                        <div className="font-['Space_Grotesk'] text-lg font-black uppercase tracking-[0.3em] text-[#8cacff] animate-pulse">
                            CONNECTING TO SERVER...
                        </div>
                    </div>
                ) : error ? (
                    <div className="grid min-h-[18rem] place-items-center text-center">
                        <div className="max-w-md">
                            <p className="mb-6 font-['Space_Grotesk'] text-lg font-black uppercase tracking-[0.2em] text-[#ff716c]">
                                {error}
                            </p>
                            <button
                                type="button"
                                onClick={handleRefresh}
                                className="bg-gradient-to-r from-[#8cacff] to-[#769dff] px-8 py-3 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-[#001f55] shadow-[0_0_15px_rgba(140,172,255,0.4)] transition-all hover:brightness-110"
                            >
                                RETRY LEADERBOARD
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <section className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            <div className="order-2 flex flex-col justify-end md:order-1">
                                <div className="relative bg-[#131313] p-6">
                                    <div className="absolute -top-4 -left-1 font-['Space_Grotesk'] text-4xl font-black text-[#8cacff] opacity-20">
                                        {ordinal(podium[0].rank)}
                                    </div>
                                    <div className="mb-6 flex items-start gap-4">
                                        <div className="flex h-16 w-16 items-center justify-center overflow-hidden border border-[#8cacff]/40 bg-[#252626]">
                                            <span className="font-['Space_Grotesk'] text-2xl font-black uppercase text-[#8cacff]">
                                                {podium[0].name.charAt(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <h3 className="font-['Space_Grotesk'] text-xl font-black uppercase tracking-tight text-white">
                                                {podium[0].name}
                                            </h3>
                                            <span className="inline-flex bg-[#8cacff]/10 px-2 py-0.5 font-['Space_Grotesk'] text-[9px] font-black uppercase tracking-widest text-[#8cacff]">
                                                Rank 02
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="font-mono text-[9px] uppercase text-[#acabaa]">System Level</p>
                                            <p className="font-['Space_Grotesk'] text-lg font-black text-[#8cacff]">
                                                LVL {podium[0].level}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-mono text-[9px] uppercase text-[#acabaa]">Accrued XP</p>
                                            <p className="font-['Space_Grotesk'] text-lg font-black text-white">
                                                {podium[0].xp.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="order-1 md:order-2">
                                <div className="relative overflow-hidden border-t-8 border-[#00cffc] bg-[#1f2020] p-8">
                                    <div className="absolute -top-2 -right-2 font-['Space_Grotesk'] text-7xl font-black text-[#00cffc] opacity-10">
                                        {ordinal(podium[1].rank)}
                                    </div>
                                    <div className="flex flex-col items-center text-center">
                                        <div className="relative mb-6 h-24 w-24">
                                            <div className="absolute inset-0 bg-[#00cffc]/20 blur-xl" />
                                            <div className="relative flex h-full w-full items-center justify-center overflow-hidden border-2 border-[#00cffc] bg-[#252626] shadow-[0_0_20px_rgba(0,207,252,0.3)]">
                                                <span className="font-['Space_Grotesk'] text-3xl font-black uppercase text-[#00cffc]">
                                                    {podium[1].name.charAt(0)}
                                                </span>
                                            </div>
                                            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#00cffc] px-3 py-1 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-tight text-[#0e0e0e]">
                                                Apex Operator
                                            </div>
                                        </div>
                                        <h2 className="mb-1 font-['Space_Grotesk'] text-3xl font-black uppercase tracking-tight text-white">
                                            {podium[1].name}
                                        </h2>
                                        <div className="mb-8 flex items-center gap-4">
                                            <span className="font-['Space_Grotesk'] text-sm font-black uppercase tracking-widest text-[#00cffc]">
                                                Rank 01
                                            </span>
                                            <span className="text-[#484848]">/</span>
                                            <span className="font-['Space_Grotesk'] text-sm font-black uppercase tracking-widest text-[#acabaa]">
                                                LVL {podium[1].level}
                                            </span>
                                        </div>
                                        <div className="mb-8 h-1 w-full bg-[#252626]">
                                            <div className="h-full w-[95%] bg-[#00cffc] shadow-[0_0_8px_rgba(0,207,252,1)]" />
                                        </div>
                                        <div className="grid w-full grid-cols-2 gap-8 border-t border-[#484848]/10 pt-6">
                                            <div className="text-left">
                                                <p className="font-mono text-[10px] uppercase text-[#acabaa]">Global XP</p>
                                                <p className="font-['Space_Grotesk'] text-2xl font-black text-[#00cffc]">
                                                    {podium[1].xp.toLocaleString()}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-mono text-[10px] uppercase text-[#acabaa]">Streak</p>
                                                <p className="font-['Space_Grotesk'] text-2xl font-black text-white">
                                                    {podium[1].streak}D
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="order-3 flex flex-col justify-end">
                                <div className="relative bg-[#131313] p-6">
                                    <div className="absolute -top-4 right-0 font-['Space_Grotesk'] text-4xl font-black text-[#d674ff] opacity-20">
                                        {ordinal(podium[2].rank)}
                                    </div>
                                    <div className="mb-6 flex items-start gap-4">
                                        <div className="flex h-16 w-16 items-center justify-center overflow-hidden border border-[#d674ff]/30 bg-[#252626]">
                                            <span className="font-['Space_Grotesk'] text-2xl font-black uppercase text-[#d674ff]">
                                                {podium[2].name.charAt(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <h3 className="font-['Space_Grotesk'] text-xl font-black uppercase tracking-tight text-white">
                                                {podium[2].name}
                                            </h3>
                                            <span className="inline-flex bg-[#d674ff]/10 px-2 py-0.5 font-['Space_Grotesk'] text-[9px] font-black uppercase tracking-widest text-[#d674ff]">
                                                Rank 03
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="font-mono text-[9px] uppercase text-[#acabaa]">System Level</p>
                                            <p className="font-['Space_Grotesk'] text-lg font-black text-[#d674ff]">
                                                LVL {podium[2].level}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-mono text-[9px] uppercase text-[#acabaa]">Accrued XP</p>
                                            <p className="font-['Space_Grotesk'] text-lg font-black text-white">
                                                {podium[2].xp.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="mt-8 space-y-4">
                            {listRows.length > 0 ? listRows.map((player) => (
                                <div
                                    key={`${player.rank}-${player.name}`}
                                    className={`flex items-center gap-4 bg-[#131313] p-5 transition-colors hover:bg-[#1f2020] ${
                                        player.isYou ? 'border-l-2 border-[#00cffc] bg-[#00cffc]/5' : 'border-l-2 border-[#484848]/15'
                                    }`}
                                >
                                    <div className="w-10 text-center font-['Space_Grotesk'] text-xl font-black italic text-[#acabaa]">
                                        #{player.rank}
                                    </div>
                                    <div className="flex h-12 w-12 items-center justify-center bg-[#252626] font-['Space_Grotesk'] text-lg font-black uppercase text-white">
                                        {player.name.charAt(0)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-3">
                                            <span className="truncate font-['Space_Grotesk'] text-sm font-black uppercase text-white">
                                                {player.name}
                                            </span>
                                            {player.isYou ? (
                                                <span className="bg-[#8cacff]/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-[#8cacff]">
                                                    You
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="mt-1 flex items-center gap-3 font-mono text-[10px] uppercase text-[#767575]">
                                            <span className="text-[#ff716c]">Fire {player.streak} day streak</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-['Space_Grotesk'] text-lg font-black italic text-white">
                                            {player.xp.toLocaleString()} XP
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#767575]">
                                            Level {player.level}
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="bg-[#131313] p-8 text-center font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-[#acabaa]">
                                    Be the first to claim a slot.
                                </div>
                            )}

                            {currentUser && !currentUserVisible ? (
                                <div className="mt-6 border-l-2 border-[#00cffc] bg-[#00cffc]/5 p-5">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 text-center font-['Space_Grotesk'] text-xl font-black italic text-[#00cffc]">
                                            #{currentUser.rank ?? '—'}
                                        </div>
                                        <div className="flex h-12 w-12 items-center justify-center bg-[#252626] font-['Space_Grotesk'] text-lg font-black uppercase text-white">
                                            {currentUser.name.charAt(0)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-3">
                                                <span className="truncate font-['Space_Grotesk'] text-sm font-black uppercase text-white">
                                                    {currentUser.name}
                                                </span>
                                                <span className="bg-[#8cacff]/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-[#8cacff]">
                                                    You
                                                </span>
                                            </div>
                                            <div className="mt-1 flex items-center gap-3 font-mono text-[10px] uppercase text-[#767575]">
                                                <span className="text-[#00cffc]">Current operator highlight</span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-['Space_Grotesk'] text-lg font-black italic text-white">
                                                {currentUser.xp.toLocaleString()} XP
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-widest text-[#767575]">
                                                Level {currentUser.level}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </section>

                        <div className="mt-16 flex items-center justify-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#484848]">
                            <span>XP resets weekly</span>
                            <span className="text-[#767575]">/</span>
                            <span>Global server time</span>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
